import { computed, ref, watch, onMounted, toValue, type Ref, type MaybeRefOrGetter, type VNodeRef } from 'vue'

import type {
  MaybeComputedElementRef,
  MaybeElement,
} from '@/composables/unrefElement'

import { unrefElement } from '@/composables/unrefElement'

/**
 * Types
 */
export type UseIntersectionObserverOptions = {
  immediate?: boolean
  root?: MaybeComputedElementRef
  rootMargin?: string
  threshold?: number | number[]
}
type UseIntersectionObserverReturn = {
  isSupported: Ref<boolean>
  stop: () => void
}
type UseIntersectionObserver = (
  target:
    | VNodeRef
    | MaybeComputedElementRef
    | MaybeRefOrGetter<MaybeElement[]>
    | MaybeComputedElementRef[],
  callback: IntersectionObserverCallback,
  options?: UseIntersectionObserverOptions
) => UseIntersectionObserverReturn
type Target = HTMLElement | SVGElement

/**
 * Constants
 */
const noop = () => {}

/**
 * Composition
 */
export const useIntersectionObserver: UseIntersectionObserver = (
  target,
  callback,
  options = {},
): UseIntersectionObserverReturn => {
  const { root, rootMargin = '0px', threshold = 0, immediate = true } = options

  const isSupported = ref<boolean>(false)
  const targets = computed<Target[]>(() => {
    const _target = toValue(target)
    return (Array.isArray(_target) ? _target : [_target])
      .map(unrefElement)
      .filter((val): val is Target => val != null)
  })

  let cleanup = noop

  const stopWatch = watch(
    () => [targets.value, unrefElement(root)] as const,
    ([targets, root]) => {
      // IntersectionObserver がサポートされていない場合は何もしない
      if (!isSupported.value) {
        return
      }

      cleanup()

      if (targets.length === 0) {
        return
      }

      const observer = new IntersectionObserver(callback, {
        root: unrefElement(root),
        rootMargin,
        threshold,
      })

      targets.forEach(el => el && observer.observe(el))

      cleanup = () => {
        observer.disconnect()
        cleanup = noop
      }
    },
    { immediate, flush: 'post' },
  )

  const stop = () => {
    cleanup()
    stopWatch()
  }

  onMounted(() => {
    isSupported.value = window && 'IntersectionObserver' in window
  })

  return {
    isSupported,
    stop,
  }
}
